// Cuándo conviene que salga un post — ADR-2026-09-15-la-mejor-hora-sale-de-tu-agenda.
//
// ── De dónde salen los números, y qué NO son ───────────────────────────────
// Meta NO publica una tabla de mejores horarios. Su guía oficial es mirar
// cuándo está activa TU audiencia en las estadísticas de la cuenta. Todo lo que
// circula como "la mejor hora para postear" sale de estudios de terceros que se
// contradicen entre sí:
//   · Buffer (9,6M posts, 2026): jueves 9:00, miércoles 12:00 y 18:00.
//   · Hootsuite (2026): miércoles 17:00, martes 5:00–8:00 y 15:00–19:00.
//   · Sprout Social (2026): martes 13:00–19:00, miércoles 12:00–21:00.
//
// Así que acá NO se copia ninguno: se toma SÓLO donde los tres coinciden, que
// es lo grueso —de martes a jueves rinde más que el fin de semana, y el mediodía
// y la tarde temprana más que la madrugada— y se deja la precisión de minutos
// afuera, porque es justo donde no se ponen de acuerdo.
//
// ── Lo que de verdad decide ────────────────────────────────────────────────
// La tabla es el desempate; lo que manda es TU AGENDA. Un miércoles ideal que ya
// tiene un post no es un miércoles ideal: dos posts el mismo día se comen entre
// ellos y es el error que esto viene a evitar. La tabla la googleás en treinta
// segundos; saber qué días te quedan libres entre todos tus lanzamientos, no.
//
// Cuando la cuenta esté conectada y podamos leer sus estadísticas reales, las
// franjas de acá tienen que ceder ante los horarios activos de ESA audiencia.
// Eso es lo único que convierte esto en un dato en vez de un promedio ajeno.
//
// Sin React y sin red, como `canales.ts`: es una regla, y las reglas se prueban.

/** Un post que ya tiene día y hora. */
export interface PostEnAgenda {
  id: string;
  cuando: Date;
}

export interface Sugerencia {
  cuando: Date;
  /** Por qué ése y no otro. Una sugerencia que no se explica no se discute. */
  porque: string;
}

export interface PedidoDeMomento {
  /** Todo lo ya planificado de la empresa, de todos los lanzamientos. */
  agenda: readonly PostEnAgenda[];
  ahora: Date;
  /** No antes de acá: la fecha del lanzamiento, si tiene. */
  desde?: Date | null;
  /**
   * No después de acá. Lo usa el calendario cuando el usuario tocó UN día: ahí
   * ya eligió el día y lo único que falta elegir es la hora. Mover el post a
   * otro día "mejor" sería desobedecer el click.
   */
  hasta?: Date | null;
  /** El post que se está editando: no compite contra sí mismo. */
  excluirId?: string | null;
}

/**
 * Cuánto pesa cada día, de domingo (0) a sábado (6).
 *
 * Martes y miércoles arriba, el fin de semana abajo: es lo único que los tres
 * estudios sostienen a la vez.
 */
const PESO_DEL_DIA = [0.55, 0.8, 1.0, 1.0, 0.95, 0.8, 0.6];

/**
 * Las horas candidatas y cuánto pesa cada una. Son DOS franjas —mediodía y
 * tarde— y no un rango continuo: a nadie le sirve que le propongan las 15:40.
 */
const HORAS_CANDIDATAS = [
  { hora: 11, peso: 0.85 },
  { hora: 12, peso: 1.0 },
  { hora: 13, peso: 0.9 },
  { hora: 17, peso: 0.9 },
  { hora: 18, peso: 1.0 },
  { hora: 19, peso: 0.85 },
] as const;

/**
 * Cuánto se castiga cada día de espera.
 *
 * Existe para que la tabla no gane siempre: sin esto, todo cae el próximo
 * martes aunque haya un jueves excelente mañana. Con 0,95 un día bueno la
 * semana que viene le gana por poco a uno mediocre mañana, y un día bueno
 * mañana le gana a cualquier cosa.
 */
const DECAIMIENTO_DIARIO = 0.95;

/** Nada dentro de las próximas dos horas: no da tiempo ni a mirarlo. */
const MINIMO_MS = 2 * 60 * 60 * 1000;

/** Si dos posts caen el mismo día, que al menos no se pisen. */
const SEPARACION_MINIMA_MS = 3 * 60 * 60 * 1000;

/** Hasta dónde se busca. Más lejos que esto ya no es una sugerencia, es un plan. */
const DIAS_A_MIRAR = 60;

const DIA_MS = 24 * 60 * 60 * 1000;

const NOMBRE_DEL_DIA = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
];

/** El mismo día del calendario, en hora local. */
function mismoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Medianoche local del día de `d`. */
function aMedianoche(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** "miércoles 12:00" — como se lo cuenta al usuario. */
export function comoSeLee(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${NOMBRE_DEL_DIA[d.getDay()]} ${hh}:${mm}`;
}

/**
 * El mejor momento libre para un post nuevo.
 *
 * Devuelve siempre algo: si los 60 días están ocupados afloja la regla de uno
 * por día y sólo respeta la separación mínima. Un formulario que se queda sin
 * proponer nada obliga al usuario a inventar una fecha, que es exactamente lo
 * que venía a evitar.
 */
export function sugerirMomento(p: PedidoDeMomento): Sugerencia {
  const ocupados = p.agenda
    .filter((a) => a.id !== p.excluirId)
    .map((a) => a.cuando)
    .sort((a, b) => a.getTime() - b.getTime());

  const piso = Math.max(
    p.ahora.getTime() + MINIMO_MS,
    p.desde ? p.desde.getTime() : 0,
  );

  const hayPostEseDia = (d: Date) => ocupados.some((o) => mismoDia(o, d));
  const seSuperpone = (d: Date) =>
    ocupados.some((o) => Math.abs(o.getTime() - d.getTime()) < SEPARACION_MINIMA_MS);

  const techo = p.hasta ? p.hasta.getTime() : Infinity;
  const primerDia = aMedianoche(new Date(Math.max(piso, p.ahora.getTime())));

  interface Candidato {
    cuando: Date;
    puntaje: number;
    diaOcupado: boolean;
  }
  const candidatos: Candidato[] = [];

  for (let i = 0; i < DIAS_A_MIRAR; i += 1) {
    const dia = new Date(primerDia.getTime() + i * DIA_MS);
    if (dia.getTime() > techo) break;
    const pesoDia = PESO_DEL_DIA[dia.getDay()] ?? 0.7;
    const diaOcupado = hayPostEseDia(dia);
    for (const { hora, peso } of HORAS_CANDIDATAS) {
      const cuando = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), hora);
      if (cuando.getTime() < piso) continue;
      if (cuando.getTime() > techo) continue;
      if (seSuperpone(cuando)) continue;
      candidatos.push({
        cuando,
        puntaje: pesoDia * peso * DECAIMIENTO_DIARIO ** i,
        diaOcupado,
      });
    }
  }

  // Primero los días enteramente libres; sólo si no queda ninguno se admite
  // compartir el día.
  const libres = candidatos.filter((c) => !c.diaOcupado);
  const enJuego = libres.length > 0 ? libres : candidatos;

  if (enJuego.length === 0) {
    // Nada entró: el día pedido está lleno, o `desde` cae después de `hasta`.
    // Se sigue devolviendo SIEMPRE algo —un formulario sin fecha propuesta
    // obliga a inventar una, que es lo que esto venía a evitar— pero se intenta
    // primero que no choque con un post existente, y se dice la verdad sobre lo
    // que se devuelve (ADR-2026-09-16-el-ultimo-recurso-no-miente).
    const tope = techo === Infinity ? piso : techo;
    const limite = Math.min(piso, tope);
    // Media hora por vez entre el piso y el techo, buscando un hueco que al
    // menos respete la separación mínima. Antes se devolvía el borde sin
    // mirarlo, y en un día lleno caía encima de otro post.
    const PASO_MS = 30 * 60 * 1000;
    for (let t = limite; t <= tope; t += PASO_MS) {
      const cuando = new Date(t);
      if (!seSuperpone(cuando)) {
        return { cuando, porque: `${comoSeLee(cuando)} — es el primer hueco que queda` };
      }
    }
    // Ni eso: el día está tapado de punta a punta. Se devuelve el borde, pero
    // avisando que hay que mirarlo en vez de llamarlo "disponible".
    const cuando = new Date(limite);
    return {
      cuando,
      porque: `${comoSeLee(cuando)} — no queda ningún hueco libre, revisalo`,
    };
  }

  const mejor = enJuego.reduce((a, b) => (b.puntaje > a.puntaje ? b : a));
  return { cuando: mejor.cuando, porque: porQueEse(mejor.cuando, ocupados, libres.length === 0) };
}

/**
 * La explicación, que nombra lo que el usuario puede verificar: el día elegido
 * y, si hubo uno, el día bueno que se salteó porque ya estaba ocupado.
 */
function porQueEse(
  elegido: Date,
  ocupados: readonly Date[],
  compartiendoDia: boolean,
): string {
  const base = comoSeLee(elegido);
  if (compartiendoDia) {
    return `${base} — no te quedan días libres, así que va separado del otro post`;
  }
  const salteado = ocupados.find(
    (o) => o.getTime() < elegido.getTime() && o.getTime() > elegido.getTime() - 7 * DIA_MS,
  );
  if (salteado) {
    return `${base} — el ${NOMBRE_DEL_DIA[salteado.getDay()]} ya tenés uno`;
  }
  return `${base} — es de los días que mejor rinden`;
}

/**
 * Cuántos posts caen ese día. Lo usa el calendario para avisar antes de guardar,
 * no para impedirlo: el usuario puede tener un motivo y no somos su jefe.
 */
export function cuantosEseDia(agenda: readonly PostEnAgenda[], dia: Date, excluirId?: string | null): number {
  return agenda.filter((a) => a.id !== excluirId && mismoDia(a.cuando, dia)).length;
}
